import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { fileUrl, uploadOne } from "@/lib/uploads";
import { useAuth } from "@/context/AuthContext";
import { Factory, Send, ClipboardList, Truck, Loader2, X, Plus, Calendar, Trash2 } from "lucide-react";
import ProjectSearchBar, { ProjectSearchEmpty, projectMatches } from "@/components/shared/ProjectSearchBar";

/**
 * FactoryOrdersPage — Feb 2026 (Factory Stage flow)
 * Landing page for Production Mgr/Exec + Procurement Mgr/Exec + Purchase Mgr/Exec.
 * Summary tiles across 5 stages (Received → Dispatched) that filter the list.
 * Dropdown drives the stage; Dispatched reveals a dispatch-details form.
 */
export const MATERIAL_CATEGORIES = [
  { key: "floor_sheets",    label: "Floor Sheets" },
  { key: "acrylic_shutter", label: "Acrylic Shutter Material" },
  { key: "hardware",        label: "Hardware Material" },
  { key: "rework",          label: "Rework Material" },
  { key: "handles",         label: "Handles" },
  { key: "panelling",       label: "Panelling Material" },
  { key: "fluted_panel",    label: "Fluted Panel" },
  { key: "cnc",             label: "CNC Material" },
  { key: "other",           label: "Other" },
];
export const matLabel = (k) => MATERIAL_CATEGORIES.find((m) => m.key === k)?.label || k;

export const FACTORY_STAGES = [
  { key: "received",           label: "Received" },
  { key: "procurement",        label: "Procurement" },
  { key: "in_progress",        label: "In Progress" },
  { key: "ready_to_dispatch",  label: "Ready to Dispatch" },
  { key: "dispatched",         label: "Dispatched" },
];
const stageLabel = (k) => FACTORY_STAGES.find((s) => s.key === k)?.label || "Received";

export default function FactoryOrdersPage() {
  const { roleName } = useAuth();
  const [orders, setOrders] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/imos/factory-queue");
      setOrders(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const map = { total: orders.length };
    FACTORY_STAGES.forEach((s) => {
      map[s.key] = orders.filter((o) => (o.factory_stage || "received") === s.key).length;
    });
    return map;
  }, [orders]);

  const filtered = useMemo(() => {
    let out = orders;
    if (stageFilter !== "all") {
      out = out.filter((o) => (o.factory_stage || "received") === stageFilter);
    }
    if (q.trim()) {
      out = out.filter((o) => projectMatches(o, q, ["flat_no"]));
    }
    return out;
  }, [orders, q, stageFilter]);

  const selected = orders.find((o) => o.id === selectedId);

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-4 sm:px-10 py-6 bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Factory Console</p>
        <h1 className="font-display text-3xl text-white mt-1">Factory Orders</h1>
        <p className="text-xs text-gray-500 mt-1">
          Signed in as <span className="text-[#d4af37]">{roleName || "Admin"}</span> — track projects across the 5 factory stages.
        </p>
      </div>

      <div className="px-4 sm:px-10 py-6 space-y-6">
        {/* Stage tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="factory-stage-tiles">
          {[
            { key: "all", label: "All Projects", value: counts.total },
            ...FACTORY_STAGES.map((s) => ({ key: s.key, label: s.label, value: counts[s.key] })),
          ].map((tile) => {
            const active = stageFilter === tile.key;
            return (
              <button key={tile.key}
                      onClick={() => setStageFilter(active && tile.key !== "all" ? "all" : tile.key)}
                      data-testid={`factory-tile-${tile.key}`}
                      className={`iz-card p-4 text-left transition-all ${active ? "border-[#d4af37] shadow-[0_0_0_1px_#d4af37_inset]" : "hover:border-[#d4af37]/40"}`}>
                <p className={`text-[10px] font-mono uppercase tracking-[0.25em] ${active ? "text-[#d4af37]" : "text-gray-500"}`}>{tile.label}</p>
                <p className={`font-display text-2xl mt-2 ${active ? "text-[#d4af37]" : "text-white"}`}>{tile.value}</p>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6 items-start">
          <div className="space-y-2">
            <ProjectSearchBar
              value={q} onChange={setQ}
              resultCount={filtered.length} totalCount={orders.length}
              testid="factory-orders-search"
            />
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-2">
              Projects ({stageFilter === "all" ? "All Stages" : stageLabel(stageFilter)})
            </p>
            {loading && <p className="text-xs text-gray-500">Loading…</p>}
            {!loading && orders.length === 0 && (
              <div className="iz-card p-6 text-center" data-testid="factory-empty">
                <Factory className="w-6 h-6 text-gray-700 mx-auto mb-2" />
                <p className="text-xs text-gray-500">No orders at Factory yet.</p>
              </div>
            )}
            {!loading && orders.length > 0 && filtered.length === 0 && q.trim() && (
              <ProjectSearchEmpty query={q} testid="factory-orders-search-empty" />
            )}
            {filtered.map((o) => {
              const stage = o.factory_stage || "received";
              return (
                <button key={o.id} onClick={() => setSelectedId(o.id)}
                        data-testid={`factory-row-${o.id}`}
                        className={`iz-card p-3 w-full text-left transition-colors hover:border-[#d4af37]/40 ${selectedId === o.id ? "border-[#d4af37]" : ""}`}>
                  <p className="text-white text-sm truncate">{o.client_name}{o.city ? ` · ${o.city}` : ""}</p>
                  <p className="text-[11px] text-gray-500 truncate">{o.project_name}</p>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">
                      {stageLabel(stage)}
                    </p>
                    {o.dispatch_count > 0 && (
                      <span className="text-[10px] font-mono text-[#d4af37] bg-[#d4af37]/10 border border-[#d4af37]/30 rounded px-1.5 py-0.5"
                            data-testid={`dispatch-count-${o.id}`}>
                        {o.dispatch_count} dispatched
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="min-w-0">
            {selected ? (
              <FactoryOrderDetail order={selected} onRefresh={load} />
            ) : (
              <div className="iz-card p-12 text-center">
                <ClipboardList className="w-8 h-8 text-gray-700 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Select a project to view files and update its stage.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function FactoryOrderDetail({ order, onRefresh }) {
  const { roleName, isAdmin, roleType, principal } = useAuth();
  const canPost = isAdmin || ["Production Manager", "Production Executive",
    "Procurement Manager", "Procurement Executive",
    "Purchase Manager", "Purchase Executive", "Director"].includes(roleName);
  const isDirector = roleType === "director";

  const currentStage = order.factory_stage || "received";
  const [stage, setStage] = useState(currentStage);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Dispatch details (for the STAGE-based dispatch on the parent order)
  const [driver, setDriver] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [material, setMaterial] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Independent dispatch history (line items)
  const [dispatches, setDispatches] = useState([]);
  const [showDispatchModal, setShowDispatchModal] = useState(false);

  const loadDispatches = useCallback(async () => {
    try {
      const { data } = await api.get(`/factory/orders/${order.id}/dispatches`);
      setDispatches(data);
    } catch { /* silent */ }
  }, [order.id]);
  useEffect(() => { loadDispatches(); }, [loadDispatches]);

  useEffect(() => { setStage(currentStage); setNote(""); setDriver(""); setVehicle(""); setMaterial(""); setAttachments([]); }, [order.id, currentStage]);

  const pickFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) uploaded.push(await uploadOne(f));
      setAttachments((prev) => [...prev, ...uploaded]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (stage === currentStage) return toast.error("Pick a new stage");
    setSaving(true);
    try {
      const body = {
        status: stageLabel(stage),
        stage,
        note: note.trim() || null,
      };
      if (stage === "dispatched") {
        body.dispatch_details = {
          driver_name: driver.trim() || null,
          vehicle_number: vehicle.trim() || null,
          material_details: material.trim() || null,
          attachment_file_ids: attachments.map((a) => a.id),
        };
      }
      await api.post(`/imos/orders/${order.id}/factory-update`, body);
      toast.success(`Stage updated to ${stageLabel(stage)}`);
      onRefresh?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const files = order.factory_files || {};
  const slots = [
    { key: "two_d", label: "2D Drawings" },
    { key: "three_d", label: "3D Views" },
    { key: "ppt", label: "PPT" },
    { key: "quotation", label: "Quotation" },
  ];

  return (
    <div className="space-y-4">
      <div className="iz-card p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Project</p>
            <h2 className="font-display text-2xl text-white mt-1">{order.client_name}{order.city ? ` · ${order.city}` : ""}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{order.project_name}</p>
          </div>
          <span className="text-[10px] font-mono uppercase tracking-widest border border-[#d4af37]/40 text-[#d4af37] px-2 py-1 rounded" data-testid="factory-current-stage">
            {stageLabel(currentStage)}
          </span>
        </div>

        {/* Key dates */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 pt-4 border-t border-[#262626]" data-testid="factory-key-dates">
          <KeyDate icon={Calendar} label="Received from IMOS"
                   value={order.imos_completed_at || order.sent_to_factory_at || order.updated_at}/>
          <KeyDate icon={Calendar} label="Carcass Delivery"
                   value={order.carcass_delivery_at}/>
          <KeyDate icon={Truck} label="First Dispatch"
                   value={order.first_dispatch_at || dispatches[dispatches.length - 1]?.dispatched_at}/>
        </div>
      </div>

      {/* IMOS handoff drawings */}
      <div className="iz-card p-5" data-testid="factory-files-readonly">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-3">IMOS Handoff Files</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {slots.map((s) => {
            const f = files[s.key];
            return (
              <div key={s.key} className="border border-[#262626] rounded px-3 py-2 flex items-center justify-between" data-testid={`factory-file-${s.key}`}>
                <div className="min-w-0">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{s.label}</p>
                  {f ? (
                    <p className="text-xs text-gray-300 truncate">{f.original_filename}</p>
                  ) : (
                    <p className="text-[11px] text-gray-600 italic">Not uploaded</p>
                  )}
                </div>
                {f && (
                  <div className="flex items-center gap-2 ml-2 shrink-0">
                    <a href={fileUrl(f.file_id)} target="_blank" rel="noreferrer"
                       className="text-[#d4af37] font-mono text-[10px] uppercase tracking-widest"
                       data-testid={`factory-file-view-${s.key}`}>View</a>
                    <a href={fileUrl(f.file_id)} download={f.original_filename}
                       className="text-white font-mono text-[10px] uppercase tracking-widest border border-[#262626] rounded px-2 py-0.5 hover:border-[#d4af37]"
                       data-testid={`factory-file-download-${s.key}`}>Download</a>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Independent Dispatches — line-item history + create */}
      <div className="iz-card p-5" data-testid="factory-dispatch-history">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Truck className="w-4 h-4 text-[#d4af37]"/>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Dispatch History ({dispatches.length})</p>
          </div>
          {canPost && (
            <button onClick={() => setShowDispatchModal(true)}
                    className="iz-btn-primary text-xs flex items-center gap-1.5"
                    data-testid="factory-open-dispatch-modal">
              <Plus className="w-3.5 h-3.5"/> Dispatch Material
            </button>
          )}
        </div>
        {dispatches.length === 0 ? (
          <p className="text-xs text-gray-500 italic">No dispatches yet.</p>
        ) : (
          <ul className="space-y-2">
            {dispatches.map((d) => {
              const canDelete = isAdmin || isDirector || d.dispatched_by === principal?.id;
              const removeDispatch = async () => {
                if (!window.confirm("Are you sure you want to delete this dispatch entry? This cannot be undone.")) return;
                try {
                  await api.delete(`/factory/dispatches/${d.id}`);
                  toast.success("Dispatch deleted");
                  onRefresh?.();
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
                }
              };
              return (
              <li key={d.id} className="border border-[#262626] rounded px-3 py-2" data-testid={`dispatch-row-${d.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm text-white">
                      {d.material_category === "other" ? d.material_other : matLabel(d.material_category)}
                    </p>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      Driver <span className="text-gray-300">{d.driver_name}</span> · Vehicle <span className="text-gray-300 font-mono">{d.vehicle_number}</span>
                    </p>
                    {d.description && <p className="text-[11px] text-gray-500 mt-1 whitespace-pre-wrap">{d.description}</p>}
                    {(d.attachment_file_ids || []).length > 0 && (
                      <p className="text-[10px] text-gray-500 mt-1">
                        {d.attachment_file_ids.map((fid, i) => (
                          <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer"
                             className="text-[#d4af37] hover:underline mr-2">
                            file {i + 1}
                          </a>
                        ))}
                      </p>
                    )}
                  </div>
                  <div className="text-right shrink-0 flex items-start gap-2">
                    <div>
                      <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">Dispatched</p>
                      <p className="text-[10px] text-gray-500">{new Date(d.dispatched_at).toLocaleString()}</p>
                      <p className="text-[10px] text-gray-500">by {d.dispatched_by_name}</p>
                    </div>
                    {canDelete && (
                      <button onClick={removeDispatch}
                              className="text-gray-500 hover:text-red-400 transition-colors mt-0.5"
                              title="Delete this dispatch entry"
                              data-testid={`dispatch-delete-${d.id}`}>
                        <Trash2 className="w-3.5 h-3.5"/>
                      </button>
                    )}
                  </div>
                </div>
              </li>
              );
            })}
          </ul>
        )}
      </div>

      {showDispatchModal && (
        <DispatchMaterialModal
          order={order}
          onClose={() => setShowDispatchModal(false)}
          onDone={() => { setShowDispatchModal(false); loadDispatches(); onRefresh?.(); }}
        />
      )}

      {/* Stage change form */}
      {canPost ? (
        <form onSubmit={submit} className="iz-card p-5" data-testid="factory-update-form">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-3">Update Carcass Delivery Stage</p>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Stage</label>
          <select value={stage} onChange={(e) => setStage(e.target.value)}
                  className="iz-input w-full mb-3" data-testid="factory-stage-select">
            {FACTORY_STAGES.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Note (optional)</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)}
                    className="iz-input w-full h-20" placeholder="Blockers, ETA, remarks…"
                    data-testid="factory-note-input"/>

          {stage === "dispatched" && (
            <div className="mt-4 space-y-3 border-t border-[#262626] pt-4" data-testid="dispatch-form">
              <div className="flex items-center gap-2 mb-1">
                <Truck className="w-4 h-4 text-[#d4af37]"/>
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Dispatch Details</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Driver Name (optional)</label>
                  <input value={driver} onChange={(e) => setDriver(e.target.value)}
                         className="iz-input w-full" data-testid="dispatch-driver-input"/>
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Vehicle Number (optional)</label>
                  <input value={vehicle} onChange={(e) => setVehicle(e.target.value)}
                         className="iz-input w-full" placeholder="KA-01-AB-1234"
                         data-testid="dispatch-vehicle-input"/>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Material Details (optional)</label>
                <textarea value={material} onChange={(e) => setMaterial(e.target.value)}
                          className="iz-input w-full h-16" placeholder="Modular carcass + accessories + hardware…"
                          data-testid="dispatch-material-input"/>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Attachments (PDF / Excel / Images)</label>
                <label className="iz-btn-secondary inline-flex items-center gap-2 cursor-pointer" data-testid="dispatch-attach-label">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <ClipboardList className="w-3.5 h-3.5"/>}
                  {uploading ? "Uploading…" : "Add Files"}
                  <input type="file" hidden multiple onChange={(e) => pickFiles(e.target.files)}
                         accept=".pdf,.xls,.xlsx,.csv,image/*,application/pdf"
                         data-testid="dispatch-attach-input"/>
                </label>
                {attachments.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {attachments.map((a) => (
                      <li key={a.id} className="text-[11px] text-gray-400 font-mono flex items-center gap-2">
                        <span className="text-[#d4af37]">✓</span> {a.original_filename}
                        <button type="button" onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))}
                                className="text-gray-500 hover:text-red-400"><X className="w-3 h-3"/></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          <button type="submit" disabled={saving || stage === currentStage}
                  className="iz-btn-primary text-xs mt-4 flex items-center gap-1.5 disabled:opacity-40"
                  data-testid="factory-submit-btn">
            <Send className="w-3.5 h-3.5"/>
            {saving ? "Saving…" : "Save Stage"}
          </button>
        </form>
      ) : (
        <div className="iz-card p-4 border-[#262626]" data-testid="factory-update-readonly">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
            {roleName} · read-only viewer — factory stages are updated by Production / Procurement / Purchase roles.
          </p>
        </div>
      )}

      {/* Dispatch summary if already dispatched */}
      {order.dispatch_details && (
        <div className="iz-card p-5" data-testid="dispatch-details-summary">
          <div className="flex items-center gap-2 mb-2">
            <Truck className="w-4 h-4 text-[#d4af37]"/>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Dispatched</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <p className="text-gray-400">Driver: <span className="text-white">{order.dispatch_details.driver_name}</span></p>
            <p className="text-gray-400">Vehicle: <span className="text-white font-mono">{order.dispatch_details.vehicle_number}</span></p>
            {order.dispatch_details.material_details && (
              <p className="text-gray-400 sm:col-span-2">Material: <span className="text-gray-200">{order.dispatch_details.material_details}</span></p>
            )}
          </div>
        </div>
      )}

      <div className="iz-card p-5" data-testid="factory-updates-log">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">Update History</p>
        {(order.factory_updates || []).length === 0 ? (
          <p className="text-xs text-gray-500 italic">No updates yet.</p>
        ) : (
          <div className="space-y-2">
            {(order.factory_updates || []).slice().reverse().map((u) => (
              <div key={u.id} className="border border-[#262626] rounded px-3 py-2" data-testid={`factory-update-entry-${u.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                    <span className="text-[#d4af37]">{u.role}</span> · {u.by_name}
                  </div>
                  <p className="text-[10px] text-gray-600">{new Date(u.at).toLocaleString()}</p>
                </div>
                <p className="text-sm text-white mt-1">{u.status}</p>
                {u.stage && (
                  <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37] mt-1">→ {stageLabel(u.stage)}</p>
                )}
                {u.note && <p className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">{u.note}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KeyDate({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 shrink-0"/>
      <div className="min-w-0">
        <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{label}</p>
        <p className="text-xs text-white truncate">
          {value ? new Date(value).toLocaleDateString() : <span className="text-gray-600">—</span>}
        </p>
      </div>
    </div>
  );
}

function DispatchMaterialModal({ order, onClose, onDone }) {
  // A "truck load" — 1 driver + 1 vehicle + N material rows
  const [driver, setDriver] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [rows, setRows] = useState([
    { id: `row-${Date.now()}`, material_category: "floor_sheets", material_other: "",
      description: "", attachments: [], uploading: false },
  ]);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState({ drivers: [], vehicles: [] });

  useEffect(() => {
    // Per-project autocomplete history
    api.get(`/factory/orders/${order.id}/dispatch-suggestions`)
      .then(({ data }) => setHistory({
        drivers: data.drivers || [],
        vehicles: data.vehicles || [],
      }))
      .catch(() => setHistory({ drivers: [], vehicles: [] }));
  }, [order.id]);

  const updateRow = (idx, patch) =>
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const addRow = () =>
    setRows((prev) => [...prev, {
      id: `row-${Date.now()}-${prev.length}`,
      material_category: "floor_sheets", material_other: "",
      description: "", attachments: [], uploading: false,
    }]);
  const removeRow = (idx) =>
    setRows((prev) => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  const pickFiles = async (idx, files) => {
    if (!files?.length) return;
    updateRow(idx, { uploading: true });
    try {
      const uploaded = [];
      for (const f of files) uploaded.push(await uploadOne(f));
      setRows((prev) => prev.map((r, i) => i === idx
        ? { ...r, attachments: [...r.attachments, ...uploaded], uploading: false }
        : r));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
      updateRow(idx, { uploading: false });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    for (const r of rows) {
      if (r.material_category === "other" && !r.material_other.trim()) {
        return toast.error("Describe the material on all 'Other' rows");
      }
    }
    setSaving(true);
    try {
      const { data } = await api.post("/factory/dispatches/bulk", {
        imos_order_id: order.id,
        driver_name: driver.trim() || null,
        vehicle_number: vehicle.trim() || null,
        items: rows.map((r) => ({
          material_category: r.material_category,
          material_other: r.material_category === "other" ? r.material_other.trim() : null,
          description: r.description.trim() || null,
          attachment_file_ids: r.attachments.map((a) => a.id),
        })),
      });
      toast.success(`Truck load recorded · ${data.count} categor${data.count === 1 ? "y" : "ies"}`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="dispatch-material-modal">
      <div className="bg-[#0a0a0a] border border-[#262626] rounded-lg w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#262626]">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Dispatch Material · Truck Load</p>
            <h3 className="font-display text-xl text-white mt-0.5">{order.client_name}{order.city ? ` · ${order.city}` : ""}</h3>
            <p className="text-[11px] text-gray-500">{order.project_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="dispatch-modal-close">
            <X className="w-5 h-5"/>
          </button>
        </div>

        <datalist id={`drivers-${order.id}`} data-testid="driver-datalist">
          {history.drivers.map((d) => <option key={d} value={d} />)}
        </datalist>
        <datalist id={`vehicles-${order.id}`} data-testid="vehicle-datalist">
          {history.vehicles.map((v) => <option key={v} value={v} />)}
        </datalist>

        <form onSubmit={submit} className="overflow-y-auto p-5 space-y-4">
          {/* Truck-load meta */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">
                Driver Name (optional)
                {history.drivers.length > 0 && (
                  <span className="ml-2 text-[#d4af37] normal-case tracking-normal">· {history.drivers.length} saved</span>
                )}
              </label>
              <input value={driver} onChange={(e) => setDriver(e.target.value)}
                     list={`drivers-${order.id}`}
                     className="iz-input w-full" placeholder="Pick or type…"
                     data-testid="dispatch-driver-modal"/>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">
                Vehicle Number (optional)
                {history.vehicles.length > 0 && (
                  <span className="ml-2 text-[#d4af37] normal-case tracking-normal">· {history.vehicles.length} saved</span>
                )}
              </label>
              <input value={vehicle} onChange={(e) => setVehicle(e.target.value)}
                     list={`vehicles-${order.id}`}
                     className="iz-input w-full" placeholder="KA-01-AB-1234"
                     data-testid="dispatch-vehicle-modal"/>
            </div>
          </div>

          {/* Material rows */}
          <div className="space-y-2 border-t border-[#1a1a1a] pt-4">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
                Materials in this Load ({rows.length})
              </p>
              <button type="button" onClick={addRow}
                      className="iz-btn-secondary text-xs flex items-center gap-1"
                      data-testid="dispatch-add-row">
                + Add category
              </button>
            </div>

            {rows.map((r, idx) => (
              <div key={r.id} className="border border-[#1c1c1c] bg-[#080808] rounded-md p-3 space-y-2"
                   data-testid={`dispatch-row-${idx}`}>
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                    Row #{idx + 1}
                  </p>
                  {rows.length > 1 && (
                    <button type="button" onClick={() => removeRow(idx)}
                            className="text-gray-500 hover:text-red-400 p-1"
                            data-testid={`dispatch-remove-row-${idx}`}
                            title="Remove this material row">
                      <X className="w-3.5 h-3.5"/>
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">
                      Material Category *
                    </label>
                    <select value={r.material_category}
                            onChange={(e) => updateRow(idx, { material_category: e.target.value })}
                            className="iz-input w-full text-sm"
                            data-testid={`dispatch-category-${idx}`}>
                      {MATERIAL_CATEGORIES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                    </select>
                  </div>
                  {r.material_category === "other" && (
                    <div>
                      <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">
                        Describe Material *
                      </label>
                      <input value={r.material_other}
                             onChange={(e) => updateRow(idx, { material_other: e.target.value })}
                             className="iz-input w-full text-sm"
                             data-testid={`dispatch-other-input-${idx}`}/>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">
                    Description / Notes
                  </label>
                  <textarea value={r.description}
                            onChange={(e) => updateRow(idx, { description: e.target.value })}
                            className="iz-input w-full h-14 text-sm"
                            placeholder="e.g. 3 shutters + fitting hardware"
                            data-testid={`dispatch-description-input-${idx}`}/>
                </div>

                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">
                    Attachments (PDF / Excel / Images)
                  </label>
                  <label className="iz-btn-secondary inline-flex items-center gap-2 cursor-pointer text-xs"
                         data-testid={`dispatch-modal-attach-label-${idx}`}>
                    {r.uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <ClipboardList className="w-3.5 h-3.5"/>}
                    {r.uploading ? "Uploading…" : "Add Files"}
                    <input type="file" hidden multiple
                           onChange={(e) => pickFiles(idx, e.target.files)}
                           accept=".pdf,.xls,.xlsx,.csv,image/*,application/pdf"
                           data-testid={`dispatch-modal-attach-input-${idx}`}/>
                  </label>
                  {r.attachments.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {r.attachments.map((a) => (
                        <li key={a.id} className="text-[11px] text-gray-400 font-mono flex items-center gap-2">
                          <span className="text-[#d4af37]">✓</span> {a.original_filename}
                          <button type="button"
                                  onClick={() => updateRow(idx, { attachments: r.attachments.filter((x) => x.id !== a.id) })}
                                  className="text-gray-500 hover:text-red-400"><X className="w-3 h-3"/></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-[#262626]">
            <button type="button" onClick={onClose} className="iz-btn-secondary text-xs">Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary text-xs flex items-center gap-1.5"
                    data-testid="dispatch-modal-submit">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>}
              Confirm Dispatch{rows.length > 1 ? ` (${rows.length} rows)` : ""}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

